<template>
	<!-- Header -->
	<el-row>
		<el-col :span="24">
			<div class="base-form header">
				<h2 class="item" v-if="viewType == ViewType.Creating">
					Новая модель автомобиля
				</h2>
				<h2 class="item" v-else>
					Карточка модели автомобиля <span v-if="originVehicleModel?.name">{{ originVehicleModel.name }}</span>
				</h2>
			</div>
		</el-col>	
	</el-row>

	<div class="base-form">
		<el-row>
			<el-col :span="24">	
				<el-form ref="f" v-loading="isAsyncOperationPending" :rules="rules" :model="form">
					<el-form-item label="Модель" label-position="top" prop="name">
						<el-input placeholder="Название модели автомобиля" v-model="form.name" max="255" />
					</el-form-item>

					<el-form-item label="Марка автомобиля" label-position="top" prop="manufacturer">
						<catalog-picker-component
							ref="manufacturer"
							auth 
							v-model="form.manufacturer"
							:catalog-tab="vehicleManufacturerCatalogTab"
							placeholder="Поиск марки автомобиля по названию"
							search-url="/api/vehiclemanufacturers/search"
							@catalog-item-picked="onVehicleManufacturerFromCatalog">
							<template #label>
								{{ form.manufacturer?.name }}
							</template>
							<template #default="scope">
								{{ scope.name }}
							</template>
						</catalog-picker-component>
					</el-form-item>

					<el-form-item label="Описание" label-position="top">
						<el-input
							v-model="form.description"
							:autosize="{ minRows: 2, maxRows: 6 }"
							type="textarea"
							maxlength="5000"
							show-word-limit
							placeholder="Описание модели автомобиля"
						/>			
					</el-form-item>

					<el-form-item>
						<el-row style="width: 100%;">
							<el-col :span="16">
								<el-button type="primary" @click="onFormSubmitClick">
									Сохранить
								</el-button>	
								<el-button type="warning" @click="onFormCancelClick">
									{{ viewType == ViewType.Editing ? 'Отменить изменения' : 'Очистить форму' }} 
								</el-button>
							</el-col>
							<el-col v-if="viewType == ViewType.Editing" :span="8">
								<div style="text-align: right;">
									<el-button type="danger"
										:disabled="!isViewLoaded && isAsyncOperationPending || !isVehicleModelDeletionAllowed" 
										@click="onDeleteVehicleClick">
										Удалить автомобиль
									</el-button>
								</div>
							</el-col>
						</el-row>
						<el-row v-if="viewType == ViewType.Editing" style="width: 100%;">
							<el-col :span="16">
								&nbsp;
							</el-col>
							<el-col :span="8">
								<div v-if="isViewLoaded && !isVehicleModelDeletionAllowed" style="text-align: right; line-height: 1rem; margin-top: 1rem; width: 100%;">
									<el-text type="info" size="small">
										<el-icon><Opportunity /></el-icon>
										Нельзя удалить - модель автомобиля используется в автомобилях контрагентов
									</el-text>
								</div>
							</el-col>
						</el-row>
					</el-form-item>
				</el-form>
			</el-col>		
		</el-row>
	</div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { CirclePlus, Edit, Opportunity } from '@element-plus/icons-vue';
import { ElTable, ElTag, ElLink, ElForm, FormInstance, ElNotification, ElMessageBox } from 'element-plus';
import { reactive } from 'vue';
import VehicleModel from '@/models/VehicleModel';
import VehicleManufacturerModel from '@/models/VehicleManufacturer';
import CatalogPickerComponent from '@/components/CatalogPickerComponent.vue';
import EventHelper from '@/helpers/eventsHelper';
import VehicleModelsApiHelper from '@/helpers/api/VehicleModelsApiHelper';
import TabsDictionary from '@/variables/tabs';
import ViewType from '@/models/enums/ViewType';
import store from '@/store';
import VehicleManufacturer from '@/models/VehicleManufacturer';

interface IVehicleModelForm {
	id: number;
	name: string | null;
	description: string | null;
	manufacturer: VehicleManufacturerModel | null;
}

@Options({
	components: {
		CirclePlus, ElForm, ElTable, ElTag, ElLink,
		Opportunity, Edit,
		CatalogPickerComponent 
	},
	props: {
		params: Array
	}
})
export default class VehicleModelView extends Vue {
	/** props */
	params!: Array<any>;

	/** constants **/
	editIcon = Edit;
	vehicleModelApi = new VehicleModelsApiHelper(store.getters.jwt);
	ViewType = ViewType;
	vehicleManufacturerCatalogTab = TabsDictionary.vehicleManufacturers;
	
	/** view-models */	
	originVehicleModel: VehicleModel | null = null;	
	viewType: ViewType = this.ViewType.Creating;
	isViewLoaded = false;
	isVehicleModelDeletionAllowed = false;
	isAsyncOperationPending = false;

	form: IVehicleModelForm = {
		id: 0,
		name: null,
		description: null,
		manufacturer: null
	};

	rules = reactive({
		name: [
			{ required: true, validator: (a: any, b: string, c: Function) => this.vehicleModelNameValidator(a, b, c), trigger: 'blur' }
		],
		manufacturer: [
			{ required: true, message: 'Производитель модели должен быть указан', trigger: 'blur' }
		]
	});

	mounted() {			
		const vehicleModel = this.params?.length && this.params[1] === true ? this.params[0] as VehicleModel : undefined;
		this.initView(vehicleModel?.id);
	}

	initView(vehicleModelId?: number) {
		if (!vehicleModelId) {
			this.viewType = ViewType.Creating;
		}
		else {
			this.viewType = ViewType.Editing;
			this.isAsyncOperationPending = true;
			this.loadVehicleModel(vehicleModelId)
				.then(vehicleModel => {

					if (!vehicleModel?.id) {
						ElNotification({
							message: 'Ошибка загрузки карточки модели автомобиля',
							type: 'error',
						});
						return;
					}

					this.originVehicleModel = vehicleModel;

					this.form.id = this.originVehicleModel.id;
					this.form.name = this.originVehicleModel.name;
					this.form.description = this.originVehicleModel.description;
					this.form.manufacturer = this.originVehicleModel.manufacturer;

					const vehicleModelPickerComponent = this.$refs.manufacturer as any;
					vehicleModelPickerComponent.set(new VehicleManufacturer(this.originVehicleModel!.manufacturer));	
					
					this.vehicleModelApi
						.isDeletableAsync(this.originVehicleModel.id)
						.then(isDeletable => this.isVehicleModelDeletionAllowed = isDeletable ?? false);
				})
				.finally(() => {
					this.isAsyncOperationPending = false;
					this.isViewLoaded = true;
				})
		}
	}

	onVehicleManufacturerFromCatalog(vehicleManufacturer: VehicleManufacturer) {
		var manufacturerPickerComponent = this.$refs.manufacturer as CatalogPickerComponent;
		manufacturerPickerComponent.set(vehicleManufacturer);
	}

	onFormSubmitClick() {		
		const form = this.$refs.f as FormInstance;
		form.validate(async (valid) => {
			if (!valid) {
				return;
			}

			const vehicleRequest = {
				id: this.form.id,
				name: this.form.name,
				description: this.form.description,
				manufacturerId: this.form.manufacturer!.id
			};
	
			const isCreatingModeOn = this.viewType == ViewType.Creating;
			const vehicleAsyncOperationHandler = isCreatingModeOn ? this.createVehicleModel : this.updateVehicleModel;
			
			try {		
				this.isAsyncOperationPending = true;
				const response = await vehicleAsyncOperationHandler(vehicleRequest);

				if (response != null) {
					EventHelper.dispatchEvent('vehicleModelsListUpdate');
					ElNotification({
						message: `Модель автомобиля ${this.form.name} успешно ${isCreatingModeOn ? 'создана' : 'изменена'}`,
						type: 'success',
					});

					if (isCreatingModeOn) {
						this.initView(response.id);
					} else {
						this.initView(this.originVehicleModel!.id);
					}
				} else {
					throw new Error();
				}
			}
			catch {
				ElNotification({
					message: `Ошибка ${(isCreatingModeOn ? 'создания' : 'изменения')} модели автомобиля ${this.form.name}`,
					type: 'error',
				});
			} finally {
				this.isAsyncOperationPending = false;
			}
		});
	}

	onFormCancelClick() {
		ElMessageBox.confirm(
				'Все несохраненные данные буду потеряны. Продолжить ?',
				{
					confirmButtonText: 'OK',
					cancelButtonText: 'Отмена',
					type: 'warning',
					confirmButtonClass: 'messagebox-danger-button'
				}
			)
			.then(() => {				
				if (this.viewType == ViewType.Creating) {
					this.form.id = 0;
					this.form.name = null;
					this.form.description = null;
					this.form.manufacturer = null;

					this.$nextTick()
						.then(() => (this.$refs.manufacturer as any).set(null));
						
				} else {
					this.form.name = this.originVehicleModel!.name;
					this.form.description = this.originVehicleModel!.description;
					this.form.manufacturer = this.originVehicleModel!.manufacturer;

					this.$nextTick()
						.then(() =>
							(this.$refs.manufacturer as any).set(new VehicleManufacturer(this.originVehicleModel!.manufacturer))
						);
				}


			});
	}

	onDeleteVehicleClick() {
		ElMessageBox.confirm(
			`Модель автомобиля ${this.originVehicleModel?.name} будет удалена. Продолжить ?`, {
			confirmButtonText: 'OK',
			cancelButtonText: 'Отмена',
			type: 'warning',
		})
		.then(async () => {
			this.isAsyncOperationPending = true;
			const isDeleted = await this.deleteVehicleModel(this.originVehicleModel!);
			if (isDeleted) {
				this.closeCurrentTab();
				ElNotification({				
					message: `Модель автомобиля ${this.originVehicleModel!.name} удалена`,
					type: 'success',
				});
			} else {
				ElNotification({				
					message: `Ошибка удаления модели автомобиля ${this.originVehicleModel!.name}`,
					type: 'error',
				});
			}
			this.isAsyncOperationPending = false;
		});
	}

	createVehicleModel(request: any) : Promise<VehicleModel | null> {
		return this.vehicleModelApi.createAsync(request);
	}

	loadVehicleModel(id: number) : Promise<VehicleModel | null> {
		return this.vehicleModelApi.getModelByIdAsync(id);
	}

	updateVehicleModel(request: any) : Promise<VehicleModel | null> {
		return this.vehicleModelApi.updateAsync(request);
	}

	deleteVehicleModel(vehicleModel: VehicleModel) : Promise<boolean> {		
		return this.vehicleModelApi.deleteAsync(vehicleModel.id);
	}

	closeCurrentTab() {		
		store.commit('removeTab', store.getters.tab);
	}
	
	async vehicleModelNameValidator(_: any, modelName: string, callback: Function) {
		if (!modelName) {
			callback(new Error('Название модели должно быть указано'));
			return false;
		}

		if (!this.form.manufacturer?.id) {
			return false;
		}

		try {
			let vehicleExists;
			if (this.viewType == ViewType.Creating) {
				vehicleExists = await this.vehicleModelApi.isAlreadyExistAsync(this.form.name!, this.form.manufacturer!.id);
			} else {
				vehicleExists = await this.vehicleModelApi.isAlreadyExistAsync(this.form.name!, this.form.manufacturer!.id, this.form.id);
			}
			
			if (vehicleExists) {
				callback(new Error('Такая модель уже существует'));
				return false;
			} 
			return true;			
		} catch {
			return true;
		}
	}	
}
</script>

<style scoped>
.messagebox-danger-button {
	border-color: var(--el-color-error);
	background: var(--el-color-error-light-3);
}

	.messagebox-danger-button:hover {
		border-color: var(--el-color-error);
		background: var(--el-color-danger);
	}
</style>